/**
 * API for the Broker Data Pipeline audit section (ADR-047 §3.3, §3.6): each writer's hourly activity beside its declared
 * daily budget, its quarantine notes, and what the writers put on the topic. The one write is W-FMCSA's Run now, which the
 * server allows to Gluon admins only.
 *
 * The response shapes come from `@numeo/types`: main-service produces them and these pages
 * render them, so a second declaration here would compile while drifting from the server.
 */

import type {
    BrokerPipelineConsumerRefusalsResponse,
    BrokerPipelineOverviewResponse,
    BrokerPipelineQuarantineDetail,
    BrokerPipelineQuarantineResponse,
    BrokerPipelineRunStatus,
    BrokerPipelineRunTrigger,
    BrokerPipelineTopicResponse,
    BrokerPipelineWriter,
    BrokerPipelineWriterActivityResponse,
    BrokerSearchConnectionsResponse,
    BrokerSearchInspectorResponse,
    BrokerSearchListResponse,
    BrokerSearchListSort,
    BrokerSearchStatus,
} from '@numeo/types-core'
import axios from 'axios'

interface Envelope<T> {
    success: boolean
    data: T
    message?: string
}

const BASE = '/gluon/broker-pipeline'

export const brokerPipelineApi = {
    /** W-FMCSA's run: whether one is going on any instance, when the last started and completed, and what it did. */
    getFmcsaRunStatus: async (): Promise<BrokerPipelineRunStatus> => {
        const res = await axios.get<Envelope<BrokerPipelineRunStatus>>(`${BASE}/fmcsa-register/status`)
        return res.data.data
    },

    /**
     * Starts W-FMCSA's weekly run now (FMCSA, then DAT per MC), or a dry run that publishes nothing; with a limit, only on
     * the register's first that many MCs. Gluon admins only.
     */
    startFmcsaRun: async (dryRun: boolean, limit?: number): Promise<BrokerPipelineRunTrigger> => {
        const res = await axios.post<Envelope<BrokerPipelineRunTrigger>>(`${BASE}/fmcsa-register/run`, { dryRun, limit })
        return res.data.data
    },

    /** Every writer's declaration: built or not, its ADR, its daily budget. Reads nothing. */
    getWriters: async (): Promise<BrokerPipelineWriter[]> => {
        const res = await axios.get<Envelope<{ writers: BrokerPipelineWriter[] }>>(`${BASE}/writers`)
        return res.data.data.writers
    },

    /** Every writer's window totals, its budget and whether it is built. */
    getOverview: async (hours: number): Promise<BrokerPipelineOverviewResponse> => {
        const res = await axios.get<Envelope<BrokerPipelineOverviewResponse>>(`${BASE}/overview`, { params: { hours } })
        return res.data.data
    },

    /** One writer's hourly counters, from its own `brokerpipe_<writer>_activity` collection. */
    getWriterActivity: async (writer: string, hours: number): Promise<BrokerPipelineWriterActivityResponse> => {
        const res = await axios.get<Envelope<BrokerPipelineWriterActivityResponse>>(`${BASE}/writer/activity`, { params: { writer, hours } })
        return res.data.data
    },

    getTopic: async (hours: number): Promise<BrokerPipelineTopicResponse> => {
        const res = await axios.get<Envelope<BrokerPipelineTopicResponse>>(`${BASE}/topic`, { params: { hours } })
        return res.data.data
    },

    getQuarantine: async (hours: number, writer?: string, reason?: string): Promise<BrokerPipelineQuarantineResponse> => {
        const res = await axios.get<Envelope<BrokerPipelineQuarantineResponse>>(`${BASE}/quarantine`, { params: { hours, writer, reason } })
        return res.data.data
    },

    /** What C-BROKER-SEARCH refused in the window (ADR-047 §3.5): counts per reason and the newest refusals. */
    getConsumerRefusals: async (hours: number): Promise<BrokerPipelineConsumerRefusalsResponse> => {
        const res = await axios.get<Envelope<BrokerPipelineConsumerRefusalsResponse>>(`${BASE}/consumer/refusals`, { params: { hours } })
        return res.data.data
    },

    /** One brokerage as the search consumer folded it, and a page of its contacts. */
    getBrokerage: async (mc: string, cohort?: string, page = 0): Promise<BrokerSearchInspectorResponse> => {
        const res = await axios.get<Envelope<BrokerSearchInspectorResponse>>(`${BASE}/consumer/brokerage/${encodeURIComponent(mc)}`, {
            params: { cohort, page },
        })
        return res.data.data
    },

    /** One page of brokerages from C-BROKER-SEARCH's store: `q` is an MC prefix when it is digits, else a name prefix. */
    listBrokerages: async (params: {
        q?: string
        status?: BrokerSearchStatus
        sort?: BrokerSearchListSort
        dir?: 'asc' | 'desc'
        page?: number
        limit?: number
    }): Promise<BrokerSearchListResponse> => {
        const res = await axios.get<Envelope<BrokerSearchListResponse>>(`${BASE}/consumer/brokerages`, { params })
        return res.data.data
    },

    /** The brokerages one shares a domain, an email or phone, or a family with. */
    getConnections: async (mc: string): Promise<BrokerSearchConnectionsResponse> => {
        const res = await axios.get<Envelope<BrokerSearchConnectionsResponse>>(`${BASE}/consumer/brokerage/${encodeURIComponent(mc)}/connections`)
        return res.data.data
    },

    /** One note whole, opened deliberately; not cached. */
    getQuarantineNote: async (id: string, writer: string): Promise<BrokerPipelineQuarantineDetail> => {
        const res = await axios.get<Envelope<BrokerPipelineQuarantineDetail>>(`${BASE}/quarantine/${encodeURIComponent(id)}`, { params: { writer } })
        return res.data.data
    },
}
